import { httpClientScreenBuilder } from '../utils/http.client';

const PRODUCT_BASE_URL = '/v1/product';

/**
 * Product Service - Handles all Product API calls
 */
export const productService = {
  /**
   * Get all Products with pagination and filters
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  getAllProducts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const url = `${PRODUCT_BASE_URL}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await httpClientScreenBuilder.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get Products by LOB ID
   * @param {string} lobId - LOB ID
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  getProductsByLobId: async (lobId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const url = `${PRODUCT_BASE_URL}/lob/${lobId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await httpClientScreenBuilder.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get a specific Product by ID
   * @param {string} productId - Product ID
   * @returns {Promise} API response
   */
  getProductById: async (productId) => {
    try {
      const response = await httpClientScreenBuilder.get(`${PRODUCT_BASE_URL}/${productId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get a Product with its sections
   * @param {string} productId - Product ID
   * @returns {Promise} API response
   */
  getProductWithSections: async (productId) => {
    try {
      const response = await httpClientScreenBuilder.get(`${PRODUCT_BASE_URL}/${productId}/sections`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a new Product
   * @param {Object} productData - Product creation data
   * @returns {Promise} API response
   */
  createProduct: async (productData) => {
    try {
      const response = await httpClientScreenBuilder.post(PRODUCT_BASE_URL, productData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing Product
   * @param {string} productId - Product ID
   * @param {Object} productData - Product update data
   * @returns {Promise} API response
   */
  updateProduct: async (productId, productData) => {
    try {
      const response = await httpClientScreenBuilder.put(`${PRODUCT_BASE_URL}/${productId}`, productData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a Product
   * @param {string} productId - Product ID
   * @returns {Promise} API response
   */
  deleteProduct: async (productId) => {
    try {
      const response = await httpClientScreenBuilder.delete(`${PRODUCT_BASE_URL}/${productId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Activate a Product
   * @param {string} productId - Product ID
   * @returns {Promise} API response
   */
  activateProduct: async (productId) => {
    try {
      const response = await httpClientScreenBuilder.put(`${PRODUCT_BASE_URL}/${productId}/activate`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Deactivate a Product
   * @param {string} productId - Product ID
   * @returns {Promise} API response
   */
  deactivateProduct: async (productId) => {
    try {
      const response = await httpClientScreenBuilder.put(`${PRODUCT_BASE_URL}/${productId}/deactivate`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update Product status
   * @param {string} productId - Product ID
   * @param {string} status - New status (DRAFT, ACTIVE, INACTIVE, ARCHIVED)
   * @returns {Promise} API response
   */
  updateProductStatus: async (productId, status) => {
    try {
      const response = await httpClientScreenBuilder.put(`${PRODUCT_BASE_URL}/${productId}/status/${status}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
